import { computed } from "vue";
import { Logger } from "@/logger";
import DraggerMenuState, {
  type PolarAngleMode,
} from "@/stores/DraggerMenuState";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import type { ObjectSnapMode } from "@/drawing/snap";

/** Incréments proposés dans le menu du polaire, avec leur rappel. */
export const POLAR_ANGLE_OPTIONS: { mode: PolarAngleMode; hint: string }[] = [
  { mode: "90", hint: "0°, 90°, 180°, 270°" },
  { mode: "45", hint: "0°, 45°, 90°, 135°…" },
  { mode: "30", hint: "0°, 30°, 60°, 90°…" },
];

/**
 * Aides au tracé de la barre d'outils : accrochage aux objets, ortho, polaire.
 * Ortho et polaire s'excluent : allumer l'un coupe l'autre.
 */
export function useSnapControls() {
  const isObjectSnapActive = computed(() => DraggerMenuState.objectSnapEnabled);
  const isOrthoActive = computed(() => DraggerMenuState.orthoMode);
  const isPolarActive = computed(() => DraggerMenuState.polarMode);
  const polarAngleMode = computed(() => DraggerMenuState.polarAngleMode);

  function toggleObjectSnap() {
    Logger.info(`useSnapControls : toggling object snap`);
    DraggerMenuState.toggleObjectSnap();
  }

  function isObjectSnapModeActive(mode: ObjectSnapMode): boolean {
    return DraggerMenuState.objectSnapModes.has(mode);
  }

  function toggleObjectSnapMode(mode: ObjectSnapMode) {
    Logger.info(`useSnapControls : toggling object snap mode ${mode}`);
    DraggerMenuState.toggleObjectSnapMode(mode);
  }

  function toggleOrtho() {
    const newOrthoState = !DraggerMenuState.orthoMode;
    Logger.info(`useSnapControls : toggling ortho mode to ${newOrthoState}`);
    DraggerMenuState.setOrthoMode(newOrthoState);
    if (newOrthoState && DraggerMenuState.polarMode) {
      DraggerMenuState.setPolarMode(false);
    }
    CanvasSettingsState.updateAndPersist();
  }

  // Rallume le polaire avec l'incrément déjà choisi.
  function togglePolar() {
    const newPolarState = !DraggerMenuState.polarMode;
    Logger.info(`useSnapControls : toggling polar mode to ${newPolarState}`);
    DraggerMenuState.setPolarMode(newPolarState);
    if (newPolarState && DraggerMenuState.orthoMode) {
      DraggerMenuState.setOrthoMode(false);
    }
    CanvasSettingsState.updateAndPersist();
  }

  // Choisir un incrément, c'est vouloir le polaire : il s'allume (et coupe
  // l'ortho) s'il était éteint.
  function setPolarAngleMode(mode: PolarAngleMode) {
    DraggerMenuState.setPolarAngleMode(mode);
    Logger.info(`useSnapControls : polar angle mode set to ${mode}°`);
    if (!DraggerMenuState.polarMode) {
      DraggerMenuState.setPolarMode(true);
      if (DraggerMenuState.orthoMode) {
        DraggerMenuState.setOrthoMode(false);
      }
    }
    CanvasSettingsState.updateAndPersist();
  }

  return {
    isObjectSnapActive,
    isOrthoActive,
    isPolarActive,
    polarAngleMode,
    toggleObjectSnap,
    isObjectSnapModeActive,
    toggleObjectSnapMode,
    toggleOrtho,
    togglePolar,
    setPolarAngleMode,
  };
}
